/**
 * MergeSelector - Merge mode selection UI
 *
 * Allows users to choose how outputs should be combined:
 * - Concatenate: Append all outputs in order
 * - Use Last: Keep only the final result
 * - Selected Only: Include only checked items
 * - AI Merge: AI combines selected parts intelligently
 *
 * Displayed as a 2x2 grid of buttons with labels and descriptions.
 */

import type { MergeMode } from '@deep-agents/shared-types';

// ============================================================================
// MERGE OPTIONS CONFIGURATION
// ============================================================================

/**
 * Configuration for each merge mode option
 */
interface MergeOption {
  value: MergeMode;
  label: string;
  description: string;
}

/**
 * All available merge mode options with their display info
 */
const MERGE_OPTIONS: MergeOption[] = [
  {
    value: 'concatenate',
    label: 'Concatenate',
    description: 'Append all outputs in order',
  },
  {
    value: 'merge-last',
    label: 'Use Last',
    description: 'Keep only the final result',
  },
  {
    value: 'user-select',
    label: 'Selected Only',
    description: 'Include only checked items',
  },
  {
    value: 'ai-merge',
    label: 'AI Merge',
    description: 'AI combines selected intelligently',
  },
];

// ============================================================================
// PROPS
// ============================================================================

interface MergeSelectorProps {
  /** Currently selected merge mode */
  mode: MergeMode;

  /** Called when user selects a different mode */
  onChange: (mode: MergeMode) => void;

  /** Disable all options (during merge operation) */
  disabled?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * MergeSelector displays merge mode options as a 2x2 button grid.
 *
 * Each button shows:
 * - Mode label (bold)
 * - Short description
 * - Selected state (indigo border/background)
 */
export function MergeSelector({ mode, onChange, disabled }: MergeSelectorProps) {
  return (
    <div className="space-y-2">
      {/* Section label */}
      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        Merge Mode
      </label>

      {/* 2x2 grid of mode buttons */}
      <div className="grid grid-cols-2 gap-2">
        {MERGE_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            disabled={disabled}
            className={`
              p-2 rounded-lg border text-left transition-all
              ${mode === option.value
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {/* Option label */}
            <div className={`text-xs font-semibold ${
              mode === option.value ? 'text-indigo-700' : 'text-gray-700'
            }`}>
              {option.label}
            </div>

            {/* Option description */}
            <div className="text-[10px] text-gray-500 mt-0.5">
              {option.description}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
